import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronUp, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Shows at the top of a dashboard when Quickstart auto-corrected column types
 * during ingestion. Surfaces the corrections and links back to the semantic model
 * so the user can review/adjust.
 *
 * Props:
 *   dataQuality: { model_id, corrections: [{ column, display_name, from, to, reason }], generated_at }
 *   onReview?: () => void  // optional override; defaults to navigating to the model page
 *   onDismiss?: () => void
 */
export function DataQualityBanner({ dataQuality, onReview, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // auto-suppress if previously dismissed for this model (hook must run unconditionally)
  React.useEffect(() => {
    if (!dataQuality) return;
    try {
      const key = `dq_dismiss_${dataQuality.model_id || 'unknown'}`;
      if (localStorage.getItem(key) === '1') setDismissed(true);
    } catch (_) {}
  }, [dataQuality]);

  if (!dataQuality || !dataQuality.corrections || dataQuality.corrections.length === 0) return null;
  if (dismissed) return null;

  const persistDismiss = () => {
    try {
      const key = `dq_dismiss_${dataQuality.model_id || 'unknown'}`;
      localStorage.setItem(key, '1');
    } catch (_) {}
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  const handleReview = () => {
    if (onReview) return onReview();
    if (dataQuality.model_id) {
      window.location.href = `/semantic-models?model=${dataQuality.model_id}`;
    }
  };

  const count = dataQuality.corrections.length;

  return (
    <div
      className="mx-6 mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 overflow-hidden"
      data-testid="data-quality-banner"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <Shield className="w-4 h-4 text-amber-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-amber-400/90">
            <span className="font-heading font-semibold">{count} column{count > 1 ? 's' : ''}</span>
            <span className="text-amber-400/70"> had ambiguous types — we auto-inferred them from your data.</span>
          </p>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="h-6 px-2 text-[10px] text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10 rounded transition-colors flex items-center gap-1"
          data-testid="dq-toggle"
        >
          {expanded ? <>Hide <ChevronUp className="w-3 h-3" /></> : <>Details <ChevronDown className="w-3 h-3" /></>}
        </button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReview}
          className="h-6 px-2 text-[10px] rounded border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
          data-testid="dq-review-btn"
        >
          <ExternalLink className="w-3 h-3 mr-1" /> Review in Model
        </Button>
        <button
          onClick={persistDismiss}
          className="h-6 w-6 flex items-center justify-center text-amber-400/50 hover:text-amber-300 hover:bg-amber-500/10 rounded transition-colors"
          data-testid="dq-dismiss"
          title="Dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-amber-500/20 bg-amber-500/5 px-3 py-2 space-y-1" data-testid="dq-details">
          {dataQuality.corrections.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px]">
              <code className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px] font-mono">
                {c.display_name || c.column}
              </code>
              <span className="text-muted-foreground/70">{c.from}</span>
              <span className="text-amber-400/60">→</span>
              <code className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[10px] font-mono">
                {c.to}
              </code>
              <span className="text-muted-foreground/80 truncate">· {c.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DataQualityBanner;
